'use client';

import React from 'react';
import Button from '../../../resuable-ui/button';
import ModalV1 from '../../../resuable-ui/modal-v1';
import { PaymentTypes } from '../../../../types';
import TransferBankModalContent from '../payment-modals/transfer-bank';
import CashCreditModalContent from '../payment-modals/cash-credit';

import styles from './index.module.scss';
import { useGetCart } from '../../../../integrations/react_query/queries';
import { EQuerykeys, ECart } from '../../../../models/index';
import { triggerToast } from '../../../../toast/index';

const PaymentButtons = () => {
	const { data: cartItems } = useGetCart([EQuerykeys.cart, ECart.active]);
	const isCustomerAttached = !!cartItems?.customer;

	const titles = {
		[PaymentTypes.card]: 'Card Payment Confirmation',
		[PaymentTypes.cash]: 'Cash Payment Confirmation',
		[PaymentTypes.credit]: 'Credit',
		[PaymentTypes.transfer]: 'Transfer Payment Confirmation',
	};

	const buttonData = [
		{ id: 2, name: PaymentTypes.card },
		{ id: 1, name: PaymentTypes.cash },
		{ id: 4, name: PaymentTypes.transfer },
		{ id: 3, name: PaymentTypes.credit, buttonText: 'Customer Account' },
	];

	const [show, setShow] = React.useState(false);
	const [type, setType] = React.useState<{ name: string; id: number | null }>({ id: null, name: '' });

	const handleModal = (name: string, id: number) => {
		setType({ id, name });
		setShow(true);
	};

	const renderPaymentModal = () => {
		const commonProps = {
			title: titles[type.name as PaymentTypes],
			paymentType: type.name,
			setShow: setShow,
			show: show,
		};

		if ([2, 4].includes(type?.id ?? 0)) {
			return <TransferBankModalContent {...commonProps} />;
		} else {
			return <CashCreditModalContent {...commonProps} />;
		}
	};

	const paymentModals = (
		<ModalV1
			show={show}
			onClose={() => setShow(false)}
			headerAction={<h3 className="text-lg font-semibold">{titles[type.name as PaymentTypes]}</h3>}
		>
			{renderPaymentModal()}
		</ModalV1>
	);

	return (
		<>
			{/* Mobile Modal */}
			<div>{paymentModals}</div>

			<section className={`${styles.wrapper} hidden md:block`}>
				<div className="container text-center">
					<div className="row">
						{buttonData.map(({ id, name, buttonText, ...props }) => {
							const isCredit = name === PaymentTypes.credit;

							return (
								<div className="col-6 my-3" key={id}>
									<Button
										{...props}
										type="button"
										styles="w-75 py-3 md:py-6"
										onClick={() => {
											if (isCredit && !isCustomerAttached) {
												triggerToast({
													type: 'info',
													message: 'Attach a customer to the cart before issuing credit.',
												});
												return;
											}
											handleModal(name, id);
										}}
										disabled={isCredit && !isCustomerAttached}
										style={{ fontWeight: 500 }}
									>
										{buttonText ?? name}
									</Button>
								</div>
							);
						})}
					</div>
				</div>
			</section>

			{/* Mobile Payment Type Selector - Horizontal Scrollable */}
			<section className="md:hidden bg-white border-t border-gray-200 px-4 py-4">
				<div className="flex flex-col gap-3">
					<h2 className="text-sm font-semibold text-gray-700">Select Payment Method</h2>
					<div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory">
						{buttonData.map(({ id, name, buttonText, ...props }) => {
							const isCredit = name === PaymentTypes.credit;
							const buttonLabel = buttonText ?? name;

							return (
								<button
									key={id}
									onClick={() => {
										if (isCredit && !isCustomerAttached) {
											triggerToast({
												type: 'info',
												message: 'Attach a customer to the cart before issuing credit.',
											});
											return;
										}
										handleModal(name, id);
									}}
									disabled={isCredit && !isCustomerAttached}
									className={`flex-shrink-0 snap-center px-4 py-2 rounded-full whitespace-nowrap font-medium text-sm transition-all ${
										isCredit && !isCustomerAttached
											? 'bg-gray-100 text-gray-400 cursor-not-allowed'
											: 'bg-purple-100 text-purple-700 hover:bg-purple-200 active:scale-95'
									}`}
									{...props}
								>
									{buttonLabel}
								</button>
							);
						})}
					</div>
				</div>
			</section>
		</>
	);
};

export default PaymentButtons;
